import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react';
import { OperationalSettings, DEFAULT_OPERATIONAL_SETTINGS } from '../types/operationalSettings';
import { logger } from '../services/logger';
import { useKeycloak } from './KeycloakContext';
import { loadOperationalSettings, saveOperationalSettings } from '../services/operationalSettingsApi';

// The user's profile on the backend is the source of truth for these preferences, so they
// follow the user across browsers and machines. localStorage stays in the picture for two
// reasons only:
//   1. it feeds the very first render, before the fetch lands — otherwise every reload
//      would flash the default dark theme / default layout;
//   2. three non-React readers still read it synchronously: services/logger.ts (log level),
//      services/deviceCommunication.ts (usePublicIpAsDirect) and App.tsx (toast theme).
// Every state change therefore mirrors into it; nothing reads it as the authority.
const CACHE_KEY = 'operational_view_settings';
// Which user the cache belongs to. On a shared machine the next user would otherwise see
// the previous user's theme until their own preferences arrive.
const CACHE_OWNER_KEY = 'operational_view_settings_user';

/**
 * Reads the boot cache. `cached` says whether these settings came from a cache belonging
 * to this user (as opposed to the defaults) — the one-time migration below needs to know
 * whether there is anything of the user's own worth pushing up to an empty profile.
 */
const readCachedSettings = (userID?: string): { settings: OperationalSettings; cached: boolean } => {
  try {
    const owner = localStorage.getItem(CACHE_OWNER_KEY);
    // No owner recorded = cache written before preferences moved server-side; it belongs
    // to whoever is logged in now.
    const ownedByUser = !userID || !owner || owner === userID;
    const saved = localStorage.getItem(CACHE_KEY);
    if (saved && ownedByUser) {
      return { settings: { ...DEFAULT_OPERATIONAL_SETTINGS, ...JSON.parse(saved) }, cached: true };
    }
  } catch (error) {
    console.error('Failed to load operational settings cache:', error);
  }
  return { settings: DEFAULT_OPERATIONAL_SETTINGS, cached: false };
};

interface OperationalSettingsContextType {
  settings: OperationalSettings;
  updateSettings: (updates: Partial<OperationalSettings>) => void;
  resetSettings: () => void;
  shouldUseMainFrame: () => boolean;
}

const OperationalSettingsContext = createContext<OperationalSettingsContextType | undefined>(undefined);

export const OperationalSettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { authenticated, initialized, user } = useKeycloak();
  const userID = user?.id;
  // Public pages (/pre-register) mount this provider outside the auth guard, where there
  // is no profile to read or write — it stays a pure localStorage context there.
  const canSync = initialized && authenticated;

  // Read once, at mount: the mirror effect below overwrites the cache on first render, so
  // "did this browser already have settings?" is only answerable before that happens.
  const [initialCache] = useState(() => readCachedSettings(userID));
  const hadCachedSettings = initialCache.cached;

  const [settings, setSettings] = useState<OperationalSettings>(initialCache.settings);

  // Lets the migration below save whatever is current when the fetch resolves, instead of
  // the mount-time snapshot it closed over — the user may have changed something meanwhile.
  const settingsRef = useRef(settings);
  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  // Load the authoritative preferences. The fetched blob wins key-by-key over the defaults,
  // so a preference the user has never saved keeps following the default.
  useEffect(() => {
    if (!canSync) return;

    let cancelled = false;
    (async () => {
      const stored = await loadOperationalSettings();
      if (cancelled || !stored) return; // null = backend unreachable, keep what we have

      if (Object.keys(stored).length === 0) {
        // Nothing saved server-side yet. Push this browser's existing preferences up once,
        // so moving to server-side storage doesn't reset the look the user already had.
        if (hadCachedSettings) {
          void saveOperationalSettings(settingsRef.current);
        }
        return;
      }

      setSettings({ ...DEFAULT_OPERATIONAL_SETTINGS, ...stored });
    })();

    return () => {
      cancelled = true;
    };
  }, [canSync, hadCachedSettings]);

  // Mirror into the cache for the next first paint and for the synchronous readers.
  useEffect(() => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(settings));
      if (userID) {
        localStorage.setItem(CACHE_OWNER_KEY, userID);
      }
      logger.setLogLevel(settings.logLevel);
    } catch (error) {
      console.error('Failed to save operational settings:', error);
    }
  }, [settings, userID]);

  // App.tsx styles the toasts from this; without the event the theme only caught up on a
  // full reload, which it no longer does now that the theme can also arrive from the backend.
  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent('panther:theme-changed', { detail: { isLight: settings.theme === 'light' } })
    );
  }, [settings.theme]);

  const updateSettings = useCallback((updates: Partial<OperationalSettings>) => {
    setSettings(prev => ({ ...prev, ...updates }));
    if (!canSync) return;
    // Only the changed keys: the backend merges them, and a failed save leaves the local
    // value in place (cached) rather than reverting under the user.
    void saveOperationalSettings(updates);
  }, [canSync]);

  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_OPERATIONAL_SETTINGS);
    if (!canSync) return;
    // Empty object = clear the stored preferences, so other devices fall back to defaults too.
    void saveOperationalSettings({});
  }, [canSync]);

  const shouldUseMainFrame = useCallback(() => {
    return settings.registrationFormDisplay === 'main-frame';
  }, [settings]);

  const value = useMemo(
    () => ({ settings, updateSettings, resetSettings, shouldUseMainFrame }),
    [settings, updateSettings, resetSettings, shouldUseMainFrame]
  );

  return (
    <OperationalSettingsContext.Provider value={value}>
      {children}
    </OperationalSettingsContext.Provider>
  );
};

export const useOperationalSettings = () => {
  const context = useContext(OperationalSettingsContext);
  if (!context) {
    throw new Error('useOperationalSettings must be used within OperationalSettingsProvider');
  }
  return context;
};
